import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSportsData } from "../contexts/SportsDataContext";
import { useFavorites } from "../contexts/FavoritesContext";
import { useVideoPlayer } from "../contexts/VideoPlayerContext";
import { useAuth } from "../contexts/AuthContext";
import { useScoreNotifications } from "../hooks/useScoreNotifications";
import GameCard from "../components/GameCard";
import { GameCardSkeleton } from "../components/Skeletons";
import { Play, ArrowRight, Lightning, Trophy, Star, Clock, MapPin, FlagCheckered, Bell, BellRinging, CalendarBlank } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map US states/regions to nearby team city keywords
const REGION_TEAMS = {
  "California": ["LAL","LAC","GSW","SAC","LAR","SF","SD","LAD","LAA","SJ"],
  "New York": ["NYK","NYM","NYG","NYJ","BKN","NYR","NYI","NYCFC"],
  "Texas": ["DAL","HOU","SA","TEX","ATX"],
  "Florida": ["MIA","ORL","TB","JAX","FLA"],
  "Illinois": ["CHI","CHC","CWS"],
  "Massachusetts": ["BOS","NE"],
  "Pennsylvania": ["PHI","PIT"],
  "Ohio": ["CLE","CIN","CBJ"],
  "Michigan": ["DET"],
  "Georgia": ["ATL"],
  "Colorado": ["DEN","COL"],
  "Arizona": ["PHX","ARI"],
  "Washington": ["SEA"],
  "Minnesota": ["MIN"],
  "Wisconsin": ["MIL","GB"],
  "Indiana": ["IND"],
  "Tennessee": ["MEM","NSH"],
  "North Carolina": ["CHA","CAR"],
  "Missouri": ["KC","STL"],
  "Oregon": ["POR"],
  "Utah": ["UTA","UTAH"],
  "Oklahoma": ["OKC"],
  "Maryland": ["BAL","WSH"],
  "District of Columbia": ["WSH","WAS"],
  "Louisiana": ["NO","NOP"],
};

const LEAGUE_FILTERS = [
  { key: "all", name: "ALL SPORTS" },
  { key: "nfl", name: "NFL" },
  { key: "nba", name: "NBA" },
  { key: "mlb", name: "MLB" },
  { key: "nhl", name: "NHL" },
  { key: "epl", name: "EPL" },
  { key: "mls", name: "MLS" },
  { key: "pga", name: "GOLF" },
  { key: "nascar", name: "NASCAR" },
  { key: "ufc", name: "UFC" },
  { key: "ncaaf", name: "NCAAF" },
  { key: "ncaab", name: "NCAAB" },
];

export default function Dashboard() {
  const { allGames, loading, userLocation, lastUpdated } = useSportsData();
  const { isFavorite } = useFavorites();
  const { playVideo } = useVideoPlayer();
  const { user } = useAuth();
  const { isEnabled: isNotifEnabled, setEnabled: setNotifEnabled } = useScoreNotifications();
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState("all");
  const [highlights, setHighlights] = useState([]);
  const [nascarInfo, setNascarInfo] = useState(null);
  const [nextUpCache, setNextUpCache] = useState({});
  const [dateSearch, setDateSearch] = useState("");
  const [dateGames, setDateGames] = useState(null);
  const [heroVideoIdx, setHeroVideoIdx] = useState(0);
  const [updatedTick, setUpdatedTick] = useState(0);
  const heroFailedRef = useRef(new Set());
  const showFavorites = searchParams.get("tab") === "favorites";

  const notifOn = isNotifEnabled();

  // User's favorite teams and sports from account preferences
  const userFavTeams = user?.preferences?.favorite_teams || [];
  const userFavSports = user?.preferences?.favorite_sports || [];

  useEffect(() => {
    axios.get(`${API}/highlights`).then((r) => setHighlights(r.data.highlights || [])).catch(() => {});
    // Pre-fetch NASCAR info on load
    axios.get(`${API}/nascar-info`).then((r) => setNascarInfo(r.data)).catch(() => {});
  }, []);

  // Fetch upcoming games lazily and cache them per league
  const nextUpGames = nextUpCache[activeFilter] || [];
  useEffect(() => {
    if (activeFilter === "all" || activeFilter === "nascar") return;
    if (nextUpCache[activeFilter]) return; // already cached
    axios.get(`${API}/upcoming/${activeFilter}`).then((r) => {
      setNextUpCache((prev) => ({ ...prev, [activeFilter]: r.data.events || [] }));
    }).catch(() => {});
  }, [activeFilter, nextUpCache]);

  // Date search
  useEffect(() => {
    if (!dateSearch) { setDateGames(null); return; }
    axios.get(`${API}/scores-by-date/${dateSearch}`).then((r) => setDateGames(r.data.games || [])).catch(() => setDateGames([]));
  }, [dateSearch]);

  // Tick every 15s so "Updated X ago" stays fresh
  useEffect(() => {
    const t = setInterval(() => setUpdatedTick((x) => x + 1), 15000);
    return () => clearInterval(t);
  }, []);

  // Cycle hero highlight thumbnail every 8s for a "live TV" feel
  useEffect(() => {
    if (highlights.length < 2) return;
    const t = setInterval(() => setHeroVideoIdx((i) => (i + 1) % highlights.length), 8000);
    return () => clearInterval(t);
  }, [highlights.length]);

  const sourceGames = dateSearch && dateGames ? dateGames : allGames;

  // On the "All Sports" view, hide pre-season scheduled games more than 14 days out
  // so live + this-week's games dominate the screen
  const fourteenDaysOut = Date.now() + 14 * 24 * 60 * 60 * 1000;
  const sourceGamesPruned = (activeFilter === "all" && !dateSearch && !showFavorites)
    ? sourceGames.filter((g) => {
        if (g.statusState !== "pre" || !g.date) return true;
        const d = Date.parse(g.date);
        return Number.isFinite(d) ? d < fourteenDaysOut : true;
      })
    : sourceGames;

  const filteredGames = activeFilter === "all"
    ? sourceGamesPruned
    : sourceGamesPruned.filter((g) => g.league === activeFilter);

  const displayGames = showFavorites
    ? filteredGames.filter((g) => isFavorite(g.id))
    : filteredGames;

  // Check if a game involves a local team
  const isLocalGame = (game) => {
    if (!userLocation) return false;
    const region = userLocation.region || "";
    const city = (userLocation.city || "").toLowerCase();
    
    // Check by region abbreviation mapping
    const regionAbbrs = REGION_TEAMS[region] || [];
    const homeAbbr = (game.homeTeam?.abbreviation || "").toUpperCase();
    const awayAbbr = (game.awayTeam?.abbreviation || "").toUpperCase();
    if (regionAbbrs.some(a => homeAbbr.includes(a) || awayAbbr.includes(a))) return true;
    
    // Check by city name match in team name/displayName
    if (city.length >= 3) {
      const homeName = (game.homeTeam?.displayName || game.homeTeam?.name || "").toLowerCase();
      const awayName = (game.awayTeam?.displayName || game.awayTeam?.name || "").toLowerCase();
      if (homeName.includes(city) || awayName.includes(city)) return true;
    }
    return false;
  };

  // Check if a game involves a user's favorite team
  const isUserFavoriteGame = (game) => {
    if (userFavTeams.length === 0) return false;
    const homeId = game.homeTeam?.id || "";
    const awayId = game.awayTeam?.id || "";
    const homeAbbr = (game.homeTeam?.abbreviation || "").toUpperCase();
    const awayAbbr = (game.awayTeam?.abbreviation || "").toUpperCase();
    return userFavTeams.some((ft) => ft.id === homeId || ft.id === awayId || (ft.abbreviation || "").toUpperCase() === homeAbbr || (ft.abbreviation || "").toUpperCase() === awayAbbr);
  };

  // Check if a game is in a user's favorite sport
  const isUserFavoriteSport = (game) => {
    if (userFavSports.length === 0) return false;
    return userFavSports.includes(game.league);
  };

  // Sort: live first, then user favorites, then favorite sport, then local, then rest
  const sortedGames = [...displayGames].sort((a, b) => {
    const stateOrder = { in: 0, pre: 2, post: 4 };
    const aState = stateOrder[a.statusState] ?? 5;
    const bState = stateOrder[b.statusState] ?? 5;
    // Primary: status (live first)
    if (aState !== bState) return aState - bState;
    // Secondary: user's favorite teams
    const aFavTeam = isUserFavoriteGame(a) ? 0 : 1;
    const bFavTeam = isUserFavoriteGame(b) ? 0 : 1;
    if (aFavTeam !== bFavTeam) return aFavTeam - bFavTeam;
    // Tertiary: user's favorite sports
    const aFavSport = isUserFavoriteSport(a) ? 0 : 1;
    const bFavSport = isUserFavoriteSport(b) ? 0 : 1;
    if (aFavSport !== bFavSport) return aFavSport - bFavSport;
    // Quaternary: local games
    const aLocal = isLocalGame(a) ? 0 : 1;
    const bLocal = isLocalGame(b) ? 0 : 1;
    return aLocal - bLocal;
  });

  const liveGames = sortedGames.filter((g) => g.statusState === "in");

  return (
    <div data-testid="dashboard">
      {/* Hero - sticky */}
      <div className="sticky top-[56px] z-30 mx-auto max-w-[1400px] px-4 sm:px-6 pt-6">
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0A0A0A]">
          <div className="flex items-center">
            {/* Video player on the left with ESPN overlay frame */}
            <div className="flex-shrink-0 w-[140px] sm:w-[220px] md:w-[280px] lg:w-[340px] relative">
              <div className="relative m-1">
                {/* ESPN frame overlay - on top */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_scoreboard-stream/artifacts/al8h4vby_Copilot_20260419_123832.png"
                  alt=""
                  className="relative w-full h-auto z-10 pointer-events-none"
                />
                {/* Hero poster — clicking opens highlights in the persistent player.
                    Cycles thumbnails every 8s to keep the frame feeling alive. */}
                {highlights.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      heroFailedRef.current = new Set();
                      playVideo(highlights[heroVideoIdx % highlights.length], highlights);
                    }}
                    className="absolute z-0 group/poster cursor-pointer overflow-hidden"
                    style={{ top: "12%", left: "3.5%", width: "93%", height: "72%" }}
                    data-testid="hero-video-poster"
                  >
                    <img
                      key={highlights[heroVideoIdx % highlights.length].youtubeId}
                      src={`https://img.youtube.com/vi/${highlights[heroVideoIdx % highlights.length].youtubeId}/maxresdefault.jpg`}
                      alt={highlights[heroVideoIdx % highlights.length].title}
                      className="w-full h-full object-cover animate-fade-in"
                      onError={(e) => {
                        const id = highlights[heroVideoIdx % highlights.length].youtubeId;
                        e.currentTarget.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover/poster:from-black/70 transition-colors">
                      <div className="w-12 h-12 rounded-full bg-[#FF3B30] flex items-center justify-center group-hover/poster:scale-110 transition-transform shadow-lg">
                        <Play size={20} weight="fill" className="text-white ml-0.5" />
                      </div>
                    </div>
                    {/* Title chip at bottom */}
                    <div className="absolute bottom-1 left-2 right-2 text-left">
                      <p className="text-[10px] uppercase tracking-wider text-white/90 font-bold truncate drop-shadow-lg">
                        {highlights[heroVideoIdx % highlights.length].title}
                      </p>
                    </div>
                  </button>
                )}
              </div>
            </div>
            {/* Logo in center-right - brighter */}
            <div className="flex-1 relative min-h-[80px] sm:min-h-[130px] md:min-h-[160px] flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_scoreboard-stream/artifacts/a82wzpdx_Copilot_20260412_215512.png" 
                alt="" 
                className="h-20 sm:h-32 md:h-40 lg:h-52 w-auto object-contain opacity-80"
              />
            </div>
          </div>
        </div>

        {/* Live score text + controls under the banner */}
        <div className="flex items-center justify-between px-2 py-1.5">
          <div className="flex items-center gap-3">
            <h2 className="font-['Bebas_Neue'] text-lg sm:text-xl tracking-wider uppercase text-white">
              {dateSearch ? `SCORES: ${new Date(dateSearch + "T12:00:00").toLocaleDateString([], { month: "short", day: "numeric" })}` : showFavorites ? "YOUR FAVORITES" : "LIVE SCORES"}
            </h2>
            {liveGames.length > 0 && !showFavorites && !dateSearch && (
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-live-pulse" />
                <span className="text-[10px] tracking-widest uppercase text-[#FF3B30] font-bold">
                  {liveGames.length} LIVE
                </span>
              </div>
            )}
            {lastUpdated && !showFavorites && !dateSearch && (() => {
              // updatedTick is read so this re-evaluates as the tick changes
              void updatedTick;
              const sec = Math.max(0, Math.floor((Date.now() - lastUpdated) / 1000));
              const label = sec < 5 ? "just now" : sec < 60 ? `${sec}s ago` : `${Math.floor(sec/60)}m ago`;
              return (
                <span className="text-[10px] text-white/30 tracking-wider uppercase hidden md:inline" data-testid="last-updated">
                  · Updated {label}
                </span>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            {/* Date picker */}
            <div className="relative">
              <input
                type="date"
                value={dateSearch}
                onChange={(e) => setDateSearch(e.target.value)}
                className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                data-testid="date-search-input"
              />
              <button className="p-1.5 rounded bg-white/5 hover:bg-white/10 transition-colors" data-testid="date-search-btn">
                <CalendarBlank size={14} className="text-white/50" />
              </button>
            </div>
            {dateSearch && (
              <button onClick={() => { setDateSearch(""); setDateGames(null); }} className="text-[10px] text-[#007AFF] font-semibold" data-testid="date-clear">Today</button>
            )}
            {/* Notifications toggle */}
            <button
              onClick={() => setNotifEnabled(!notifOn)}
              className={`p-1.5 rounded transition-colors ${notifOn ? "bg-[#007AFF]/20 text-[#007AFF]" : "bg-white/5 text-white/40 hover:bg-white/10"}`}
              data-testid="notif-toggle"
              title={notifOn ? "Notifications on" : "Enable notifications"}
            >
              {notifOn ? <BellRinging size={14} weight="fill" /> : <Bell size={14} />}
            </button>
            {userLocation && !showFavorites && (
              <span className="text-[10px] text-white/30 tracking-wider uppercase hidden sm:inline">
                {userLocation.city}, {userLocation.regionCode || userLocation.region}
              </span>
            )}
          </div>
        </div>

        {/* League filter tabs */}
        <div className="flex items-center gap-1 overflow-x-auto py-1.5 px-2 scrollbar-hide bg-[#0A0A0A]/90" data-testid="league-filter-tabs">
          <button
            onClick={() => setActiveFilter("all")}
            className={`px-3 py-1.5 rounded text-[11px] tracking-widest uppercase font-semibold whitespace-nowrap transition-colors ${
              activeFilter === "all" ? "bg-[#007AFF] text-white" : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80"
            }`}
            data-testid="filter-all"
          >All Sports</button>
          {LEAGUE_FILTERS.slice(1).map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`px-3 py-1.5 rounded text-[11px] tracking-widest uppercase font-semibold whitespace-nowrap transition-colors ${
                activeFilter === f.key ? "bg-[#007AFF] text-white" : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80"
              }`}
              data-testid={`filter-${f.key}`}
            >{f.name}</button>
          ))}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-6 pb-6 space-y-8">

      {/* NASCAR Points Leaders + Last Race Winner + Next Race */}
      {activeFilter === "nascar" && nascarInfo && !showFavorites && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="nascar-info">
          {/* Points Leaders - compact top 5 with car numbers */}
          <div className="bg-[#141414] border border-white/10 rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
              <Trophy size={14} weight="fill" className="text-[#007AFF]" />
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-white/60">Points Standings</h3>
            </div>
            <div className="divide-y divide-white/5">
              {(nascarInfo.pointsLeaders || []).slice(0, 5).map((driver) => (
                <div key={driver.rank} className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 transition-colors">
                  <span className={`score-font text-xs w-5 text-center ${driver.rank === "1" ? "text-[#007AFF] font-bold" : "text-white/30"}`}>
                    {driver.rank}
                  </span>
                  {driver.carNumber && <span className="text-[10px] text-[#007AFF] font-bold w-6">#{driver.carNumber}</span>}
                  <span className={`flex-1 text-xs truncate ${driver.rank === "1" ? "text-white font-semibold" : "text-white/70"}`}>
                    {driver.shortName || driver.name}
                  </span>
                  <span className="score-font text-[10px] text-white/40">
                    {driver.rank === "1" ? "Leader" : `-${driver.deltaLeader || driver.points}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Last Race Winner */}
          {sortedGames.filter(g => g.league === "nascar" && g.statusState === "post").length > 0 && (
            <div className="bg-[#141414] border border-white/10 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
                <FlagCheckered size={14} className="text-white/50" />
                <h3 className="text-[10px] tracking-widest uppercase font-bold text-white/60">Last Race</h3>
              </div>
              {sortedGames.filter(g => g.league === "nascar" && g.statusState === "post").slice(0, 1).map((race) => (
                <div key={race.id} className="p-3 space-y-2">
                  <p className="text-sm font-semibold text-white">{race.name}</p>
                  {race.awayTeam?.winner && (
                    <div className="flex items-center gap-2 bg-[#34C759]/10 border border-[#34C759]/20 rounded px-2 py-1.5">
                      {race.awayTeam.logo && <img src={race.awayTeam.logo} alt="" className="w-5 h-5 rounded-full" />}
                      <Trophy size={12} weight="fill" className="text-[#34C759]" />
                      <span className="text-xs font-bold text-[#34C759]">WINNER</span>
                      <span className="text-xs font-semibold text-white">{race.awayTeam.displayName}</span>
                    </div>
                  )}
                  {race.top5?.slice(1, 5).map((d) => (
                    <div key={d.position} className="flex items-center gap-2 text-white/40 px-1">
                      {d.flag && <img src={d.flag} alt="" className="w-4 h-4 rounded-full" />}
                      <span className="text-[10px] w-5 text-center">{d.position}</span>
                      <span className="text-[10px]">{d.name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Next Race */}
          {nascarInfo.nextRace && (
            <div className="bg-[#141414] border border-white/10 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
                <Clock size={14} className="text-[#34C759]" />
                <h3 className="text-[10px] tracking-widest uppercase font-bold text-white/60">Next Race</h3>
              </div>
              <div className="p-3 space-y-2">
                <p className="text-sm font-semibold text-white">{nascarInfo.nextRace.name}</p>
                {nascarInfo.nextRace.venue && (
                  <p className="text-[10px] text-white/40 flex items-center gap-1">
                    <MapPin size={10} /> {nascarInfo.nextRace.venue}
                  </p>
                )}
                <p className="text-sm text-[#007AFF] font-medium">
                  {new Date(nascarInfo.nextRace.date).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
                </p>
                <p className="text-xs text-white/40">
                  {new Date(nascarInfo.nextRace.date).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* NEXT UP section for other sports when no live games in that filter */}
      {activeFilter !== "all" && activeFilter !== "nascar" && !showFavorites && nextUpGames.length > 0 && liveGames.length === 0 && (
        <div data-testid="next-up-section">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-[#34C759]" />
            <h3 className="text-xs tracking-widest uppercase font-bold text-white/60">Next Up</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {nextUpGames.slice(0, 3).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        </div>
      )}

      {/* Games grid - hide duplicates when info panels or NEXT UP are showing */}
      {(() => {
        let gamesToShow = sortedGames;
        // Hide finished NASCAR races when info panel shows them
        if (activeFilter === "nascar" && nascarInfo) {
          gamesToShow = gamesToShow.filter((g) => g.statusState !== "post");
        }
        // Hide pre games already shown in NEXT UP
        const showingNextUp = activeFilter !== "all" && activeFilter !== "nascar" && !showFavorites && nextUpGames.length > 0 && liveGames.length === 0;
        if (showingNextUp) {
          const nextUpIds = new Set(nextUpGames.slice(0, 3).map((g) => g.id));
          gamesToShow = gamesToShow.filter((g) => !nextUpIds.has(g.id));
        }
        return loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <GameCardSkeleton key={i} />
            ))}
          </div>
        ) : gamesToShow.length === 0 && !(activeFilter === "nascar" && nascarInfo) ? (
          <div className="text-center py-16">
            <Trophy size={48} className="mx-auto text-white/10 mb-4" />
            <p className="text-white/30 text-sm">
              {showFavorites ? "No favorited games yet. Star games to track them here." : "No games scheduled right now. Check back later."}
            </p>
          </div>
        ) : gamesToShow.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children" data-testid="games-grid">
            {gamesToShow.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        ) : null;
      })()}

      {/* Highlights section */}
      {!showFavorites && highlights.length > 0 && (
        <section className="space-y-4" data-testid="highlights-section">
          <div className="flex items-center justify-between">
            <h2 className="font-['Bebas_Neue'] text-2xl lg:text-3xl tracking-tight uppercase text-white flex items-center gap-2">
              <Lightning size={24} weight="fill" className="text-[#007AFF]" />
              Highlights
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {highlights.map((h) => (
              <button
                key={h.id}
                onClick={() => playVideo(h, highlights)}
                className="group bg-[#141414] border border-white/10 rounded-lg overflow-hidden text-left hover:border-white/20 transition-colors"
                data-testid={`highlight-${h.id}`}
              >
                <div className="relative aspect-video bg-black/50">
                  <img
                    src={`https://img.youtube.com/vi/${h.youtubeId}/mqdefault.jpg`}
                    alt={h.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-[#007AFF]/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play size={18} weight="fill" className="text-white ml-0.5" />
                    </div>
                  </div>
                  <span className="absolute bottom-1 right-1 bg-black/70 text-[10px] text-white/80 px-1.5 py-0.5 rounded">{h.duration}</span>
                </div>
                <div className="px-2 py-2">
                  <p className="text-xs text-white/80 font-medium line-clamp-2 leading-tight">{h.title}</p>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mt-1">{h.sport}</p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
      </div>
    </div>
  );
}
